import type {
  EncryptionMode,
  ListKind,
  PublicUser,
  SpaceRole,
  WishStatus,
} from "@vmeste/shared";
import { t } from "../i18n/index.js";

/**
 * View models — what components render.
 *
 * Content here is already decoded out of its ContentBlob (SPEC §3). Nothing
 * below this line knows whether a space is encrypted, which is what makes
 * turning on `e2ee` a change to the sync layer alone.
 */

export interface SpaceView {
  id: string;
  name: string;
  role: SpaceRole;
  memberCount: number;
  /**
   * The one thing about encryption a screen is allowed to know.
   *
   * It comes from `me` rather than from the sync state, and that is the point:
   * on a cold start the delta has not landed yet, and the lock has to be
   * decided before the first screen renders — otherwise a device without the
   * key shows a screenful of placeholders and only then admits it cannot read
   * them.
   */
  encryptionMode: EncryptionMode;
}

export interface MemberView {
  user: PublicUser;
  role: SpaceRole;
  joinedAt: string;
}

export interface ListItemView {
  id: string;
  text: string;
  note?: string;
  done: boolean;
  doneBy: string | null;
  dueAt: string | null;
  assigneeId: string | null;
}

export interface WishView {
  id: string;
  title: string;
  url?: string;
  host?: string;
  note?: string;
  price?: string;
  status: WishStatus;
  claimedBy: string | null;
  addedBy: string;
}

/** What one person said about a place: a score, and words if they had any. */
export interface PlaceReviewView {
  userId: string;
  rating: number;
  text?: string;
  updatedAt: string;
}

/**
 * A place as the map and the list render it.
 *
 * `point` is nullable, and that is not defensiveness: the coordinates are
 * inside the content blob, so a device with no key for an encrypted space has
 * a place it can list but cannot put anywhere. The row still exists and still
 * counts; it simply has no pin.
 */
export interface PlaceView {
  id: string;
  title: string;
  address?: string;
  note?: string;
  url?: string;
  point: { lat: number; lng: number } | null;
  visited: boolean;
  visitedAt: string | null;
  /** The mean of every opinion, or null while nobody has one. */
  rating: number | null;
  reviews: PlaceReviewView[];
  /** The first picture, for the card and the pin. A path on our own origin. */
  photo?: string;
  photoCount: number;
  createdBy: string;
  createdAt: string;
}

export interface ListView {
  id: string;
  kind: ListKind;
  title: string;
  icon: string;
  /** Out of the way, but not gone — see `archiveList`. */
  archived: boolean;
  items: ListItemView[];
}

/**
 * The labels are getters, not strings.
 *
 * A `Record<K, string>` filled in at module scope freezes the language at import
 * time — the words would be whichever language happened to be on when the bundle
 * loaded, and switching afterwards would change nothing. Reading a property
 * calls `t()` at that moment instead, so the shape every caller already indexes
 * into stays exactly the same while the words follow the current language.
 */
export const LIST_KIND_LABEL: Record<ListKind, string> = {
  get todo() {
    return t("lists.kindTodo");
  },
  get shopping() {
    return t("lists.kindShopping");
  },
};

export const WISH_STATUS_LABEL: Record<WishStatus, string> = {
  get idea() {
    return t("wishes.statusIdea");
  },
  get bought() {
    return t("wishes.statusBought");
  },
  get gifted() {
    return t("wishes.statusGifted");
  },
};

export const ROLE_LABEL: Record<SpaceRole, string> = {
  get owner() {
    return t("search.roleOwner");
  },
  get member() {
    return t("search.roleMember");
  },
};
